// Lightweight Dify Chat API client.
// Docs: https://docs.dify.ai/guides/application-publishing/developing-with-apis
// Endpoint: POST {base}/chat-messages  (streaming or blocking)

const BASE = process.env.REACT_APP_DIFY_BASE_URL || 'https://api.dify.ai/v1';

export function isDifyConfigured(apiKey) {
  return typeof apiKey === 'string' && apiKey && !apiKey.startsWith('YOUR_');
}

/**
 * Send a message to Dify and stream the response.
 * @param {Object} params
 * @param {string} params.apiKey - Dify app API key
 * @param {string} params.query - user query
 * @param {string} [params.conversationId] - existing conversation id
 * @param {string} [params.user] - end user id
 * @param {Object} [params.inputs] - app inputs
 * @param {(chunk: {answer?:string, event:string, conversation_id?:string, message_id?:string}) => void} params.onEvent
 * @param {AbortSignal} [params.signal]
 */
export async function streamDifyChat({
  apiKey,
  query,
  conversationId = '',
  user = 'brahmastra-user',
  inputs = {},
  onEvent,
  signal,
}) {
  if (!isDifyConfigured(apiKey)) {
    throw new Error('DIFY_NOT_CONFIGURED');
  }
  const res = await fetch(`${BASE}/chat-messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      inputs,
      query,
      response_mode: 'streaming',
      conversation_id: conversationId,
      user,
    }),
    signal,
  });

  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch { /* noop */ }
    throw new Error(`Dify ${res.status}: ${detail || res.statusText}`);
  }

  if (!res.body) throw new Error('No response body from Dify');
  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const raw of lines) {
      const line = raw.trim();
      if (!line.startsWith('data:')) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === '[DONE]') continue;
      try {
        const json = JSON.parse(payload);
        onEvent?.(json);
      } catch {
        // ignore malformed chunks
      }
    }
  }
}

/**
 * Blocking call - returns full answer at once (for quizzes/flashcards).
 */
export async function callDifyBlocking({
  apiKey,
  query,
  user = 'brahmastra-user',
  inputs = {},
  conversationId = '',
}) {
  if (!isDifyConfigured(apiKey)) {
    throw new Error('DIFY_NOT_CONFIGURED');
  }
  const res = await fetch(`${BASE}/chat-messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      inputs,
      query,
      response_mode: 'blocking',
      conversation_id: conversationId,
      user,
    }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`Dify ${res.status}: ${t || res.statusText}`);
  }
  return await res.json();
}
